import React, { useEffect, useState } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { Box, Typography, Slide, Fade, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Wifi as WifiIcon, 
  WifiOff as WifiOffIcon, 
  SignalCellularAlt as SignalIcon,
  SignalCellularAlt2Bar as SignalMediumIcon,
  SignalCellularAlt1Bar as SignalLowIcon,
  SignalCellular0Bar as SignalNoneIcon,
  Info as InfoIcon
} from '@mui/icons-material'; 
import PropTypes from 'prop-types';

// Helper function to get border color based on severity
const getBorderColor = (theme, severity) => {
  switch (severity) {
    case 'error':
      return theme.palette.error.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'success':
      return theme.palette.success.main;
    default:
      return theme.palette.info.main;
  }
};

const StyledStatusBar = styled(Box)(({ theme, severity = 'info' }) => ({
  position: 'fixed',
  bottom: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
  zIndex: theme.zIndex.snackbar,
  borderLeft: `4px solid ${getBorderColor(theme, severity)}`,
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': { boxShadow: `0 0 0 0 ${theme.palette.primary.light}40` },
    '70%': { boxShadow: `0 0 0 10px ${theme.palette.primary.light}00` },
    '100%': { boxShadow: `0 0 0 0 ${theme.palette.primary.light}00` },
  },
}));

const ConnectionSpeedIndicator = ({ downlink, rtt }) => {
  if (downlink === undefined || rtt === undefined) {
    return <SignalIcon color="disabled" />;
  }

  // Determine connection quality based on downlink and RTT
  const connectionScore = (downlink / 10) * (1 - Math.min(rtt / 200, 1));
  
  if (connectionScore > 0.7) return <SignalIcon color="success" />;
  if (connectionScore > 0.4) return <SignalMediumIcon color="warning" />;
  if (connectionScore > 0.1) return <SignalLowIcon color="warning" />;
  return <SignalNoneIcon color="error" />;
};

ConnectionSpeedIndicator.propTypes = {
  downlink: PropTypes.number,
  rtt: PropTypes.number
};

const NetworkStatusIndicator = () => {
  const {
    isOnline,
    wasOffline,
    showBackOnlineUI,
    isSlowConnection,
    effectiveType,
    downlink,
    rtt,
    saveData,
    offlineDuration
  } = useNetworkStatus();

  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [icon, setIcon] = useState(null);

  useEffect(() => {
    if (!isOnline) {
      setMessage('You are currently offline. Some features may be limited.');
      setSeverity('error');
      setIcon(<WifiOffIcon />);
      setVisible(true);
    } else if (wasOffline || showBackOnlineUI) {
      const minutes = Math.floor(offlineDuration / 60000);
      const seconds = Math.floor((offlineDuration % 60000) / 1000);
      const durationStr = minutes > 0 
        ? `${minutes}m ${seconds}s` 
        : `${seconds}s`;
      
      setMessage(`Back online! You were offline for ${durationStr}.`);
      setSeverity('success');
      setIcon(<WifiIcon />);
      setVisible(true);
      
      const timer = setTimeout(() => {
        setVisible(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    } else if (isSlowConnection) {
      setMessage(`Slow connection detected (${effectiveType || 'unknown'}). Content may load slowly.`);
      setSeverity('warning');
      setIcon(<SignalLowIcon />);
      setVisible(true);
    } else if (saveData) {
      setMessage('Data saver is enabled. Some media may be of lower quality.');
      setSeverity('info');
      setIcon(<InfoIcon />);
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [isOnline, wasOffline, showBackOnlineUI, isSlowConnection, saveData, effectiveType, offlineDuration]);

  if (!visible) return null;

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Fade in={visible}>
        <StyledStatusBar severity={severity}>
          <Box display="flex" alignItems="center" gap={1}>
            {icon}
            <Typography variant="body2" fontWeight="medium">
              {message}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" ml={2}>
            <ConnectionSpeedIndicator downlink={downlink} rtt={rtt} />
            {!isOnline && (
              <Box ml={1} display="flex" alignItems="center">
                <CircularProgress size={16} thickness={5} color="inherit" />
              </Box>
            )}
          </Box>
        </StyledStatusBar>
      </Fade>
    </Slide>
  );
};

// No prop types needed as all props come from the useNetworkStatus hook
NetworkStatusIndicator.propTypes = {};

export default NetworkStatusIndicator;
